// Move batch-tracked stock from wherever it is into one container — splitting a batch
// across bags, topping a pouch up from the cupboard, recombining what's left, or (with
// `quarantine`) setting it aside in a pending-disposal container with a reason.
// POST /stock/move; the rules are core/stock.move.
//
// Each line is one batch at one place and how many of it to move. At the destination
// it goes into the line of that drug already there (merging with the same batch if
// it's held), or a new line if there's none. Two lines of one drug in one container
// (told apart by details or size) is a choice only the person moving it can make, so
// it's asked.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, IconButton, MenuItem, Stack, TextField, Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { API_BASE_URL } from "../../config";
import { notify } from "../../notify";
import { CONTAINER_CATEGORY, QUARANTINE_REASONS, typeLabel } from "../../constants";
import {
  allHoldings, containerPath, expiryFlag, itemName, placeLabel, stockRoleOf,
} from "../../stockRules";

let nextKey = 1;
const line = (holdingId = null, quantity = "") => ({ key: nextKey++, holding_id: holdingId,
                                                    quantity, to_item_id: "" });

export default function MoveStockDialog({ open, onClose, onDone, items, types, initial }) {
  const typeById = useMemo(() => Object.fromEntries(types.map(t => [t.id, t])), [types]);
  const itemById = useMemo(() => Object.fromEntries(items.map(i => [i.id, i])), [items]);
  const holdings = useMemo(() => allHoldings(items), [items]);
  const holdingById = useMemo(() => Object.fromEntries(holdings.map(h => [h.id, h])), [holdings]);
  const quarantine = Boolean(initial?.quarantine);

  const [dest, setDest] = useState("");
  const [lines, setLines] = useState([line()]);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDest(initial?.toContainerId ?? "");
    setLines(initial?.lines?.length
      ? initial.lines.map(l => line(l.holding_id, l.quantity ?? ""))
      : [line()]);
    setReason(initial?.reason ?? ""); setNotes("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const containers = items
    .filter(i => typeById[i.type_id]?.category === CONTAINER_CATEGORY)
    .filter(i => !quarantine || stockRoleOf(i, itemById, typeById) === "quarantine")
    .sort((a, b) => containerPath(a, itemById, typeById)
      .localeCompare(containerPath(b, itemById, typeById)));

  const holdingLabel = (h) => {
    const t = typeById[h.item.type_id];
    const flag = expiryFlag(h.expiry_date, Number(t?.expiry_grace_years) || 0);
    return `${typeLabel(t) || "Item"} · batch ${h.serial_number || "—"} · expires ${h.expiry_date || "—"}`
      + `${flag?.severity === "error" ? " (expired)" : ""}${h.recalled ? " (RECALLED)" : ""}`
      + ` · ${h.quantity} at ${placeLabel(h.item, itemById, typeById)}`;
  };

  const patch = (key, p) => setLines(ls => ls.map(l => (l.key === key ? { ...l, ...p } : l)));

  // The lines of a drug already in the destination: two or more is a choice to make.
  const targets = (h) => (h && dest
    ? items.filter(i => i.location_id === dest && i.type_id === h.item.type_id) : []);

  const problem = (l) => {
    const h = holdingById[l.holding_id];
    if (!h) return "Choose what to move";
    const n = Number(l.quantity);
    if (!(n >= 1)) return "How many?";
    if (n > h.quantity) return `Only ${h.quantity} of that batch is there`;
    if (h.item.location_id === dest && targets(h).length <= 1) return "It's already there";
    if (targets(h).length > 1 && !l.to_item_id) return "Choose which line it goes into";
    return null;
  };
  const filled = lines.filter(l => l.holding_id || l.quantity);
  const problems = filled.map(problem).filter(Boolean);
  const canSave = dest && filled.length > 0 && problems.length === 0
    && (!quarantine || reason.trim()) && !saving;

  const save = () => {
    setSaving(true);
    axios.post(`${API_BASE_URL}/stock/move`, {
      to_container_id: dest,
      quarantine,
      reason: reason.trim() || null,
      notes: notes.trim() || null,
      lines: filled.map(l => ({ holding_id: l.holding_id, quantity: Number(l.quantity),
                                to_item_id: l.to_item_id || null })),
    }).then(() => {
      notify(quarantine ? "Set aside for disposal." : "Stock moved.", "success");
      onDone && onDone();
      onClose();
    }).catch(() => {}).finally(() => setSaving(false));
  };

  const chosen = new Set(lines.map(l => l.holding_id));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{quarantine ? "Set aside for disposal" : "Move stock"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <DialogContentText>
            {quarantine
              ? "Moves stock into a pending-disposal container, out of use, until it's disposed of with a waste transfer note."
              : "Moves stock between containers. A batch can be split across several, and the same batch arriving where it's already held is added to it."}
          </DialogContentText>
          {quarantine && containers.length === 0 ? (
            <Alert severity="warning">
              No container is for pending disposal yet. Set the stock role on its
              type, on the Types page, first.
            </Alert>
          ) : (
            <Autocomplete
              options={containers}
              getOptionLabel={c => containerPath(c, itemById, typeById)}
              getOptionKey={c => c.id}
              value={itemById[dest] || null}
              onChange={(_, c) => setDest(c ? c.id : "")}
              renderInput={p => <TextField {...p} label="Into" />} />
          )}

          {lines.map(l => {
            const h = holdingById[l.holding_id];
            const into = targets(h);
            return (
              <Box key={l.key}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
                       sx={{ alignItems: { sm: "flex-start" } }}>
                  <Autocomplete
                    sx={{ flex: 1 }}
                    options={holdings.filter(o => o.id === l.holding_id || !chosen.has(o.id))}
                    getOptionLabel={holdingLabel}
                    getOptionKey={o => o.id}
                    value={h || null}
                    onChange={(_, o) => patch(l.key, { holding_id: o ? o.id : null,
                                                       quantity: o ? o.quantity : "", to_item_id: "" })}
                    renderInput={p => <TextField {...p} label="Batch and where it is" size="small" />} />
                  <TextField label="Quantity" type="number" size="small" sx={{ width: 110 }}
                             slotProps={{ htmlInput: { min: 1, step: 1, max: h?.quantity } }}
                             value={l.quantity}
                             onChange={e => patch(l.key, { quantity: e.target.value })} />
                  <Tooltip title="Remove line">
                    <span>
                      <IconButton size="small" disabled={lines.length === 1}
                                  onClick={() => setLines(ls => ls.filter(x => x.key !== l.key))}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                {into.length > 1 && (
                  <TextField label="Into which line" select size="small" fullWidth sx={{ mt: 1 }}
                             value={l.to_item_id} onChange={e => patch(l.key, { to_item_id: e.target.value })}>
                    {into.map(i => (
                      <MenuItem key={i.id} value={i.id}>{itemName(i, typeById)}</MenuItem>
                    ))}
                  </TextField>
                )}
                {h && problem(l) && (
                  <Typography variant="caption" color="error">{problem(l)}</Typography>
                )}
              </Box>
            );
          })}
          <Box>
            <Button size="small" startIcon={<AddIcon />}
                    onClick={() => setLines(ls => [...ls, line()])}>Add another batch</Button>
          </Box>

          {quarantine && (
            <Autocomplete freeSolo options={QUARANTINE_REASONS} inputValue={reason}
                          onInputChange={(_, v) => setReason(v)}
                          renderInput={p => <TextField {...p} label="Why" required />} />
          )}
          <TextField label="Notes" fullWidth multiline value={notes}
                     onChange={e => setNotes(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSave} onClick={save}>
          {quarantine ? "Set aside" : "Move"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
